import React, { useCallback, useEffect, useMemo } from 'react';
import tw, { styled } from 'twin.macro';
import useAdEditorContext from 'hooks/context/ad-editor-context';
import { getOverlay } from 'utils/overlayHelpers';
import useNavigationContext from 'hooks/context/nav-context';
import Button from 'components/button/Button';
import { fireEvent } from 'utils/eventHelper';
import { showIntercom } from 'utils/intercomHelper';
import { Title3 } from 'components/typography/Typography';
import { PostHogFeature } from 'posthog-js/react';
import {
  showsLegacyDefaults,
  variantCanTakeOverlay,
  variantCanUseTemplates,
} from 'utils/templateFieldHelpers';
import { getOverlayFit, overlayIsOfferable } from 'utils/adEditorCanvasHelpers';
import { AdOverlay } from 'types/adEditorTypes';
import { TemplateValueControls } from 'components/ad-builder/TemplateValueControls';
import { withAnonymousSvgImages } from 'utils/svgImageCorsHelpers';
import { missingTileCodes } from 'utils/templatePreviewHelpers';

/**
 * A legacy overlay: its SVG is composited onto the canvas here in the browser, and the tile itself
 * is that same SVG rendered small.
 */
const OverlayPreview = ({ overlay }: { overlay: AdOverlay }) => {
  const { currentVariant, pickLegacyOverlay } = useAdEditorContext();
  const { currentVacancy } = useNavigationContext();

  const SVGComponent = useCallback(
    (svg: string) => {
      return withAnonymousSvgImages(
        getOverlay(
          svg,
          currentVariant,
          currentVacancy?.brand,
          currentVacancy?.targeting?.locations?.[0]?.city,
          false
        )
      );
    },
    [
      currentVariant?.uuid,
      currentVariant?.company,
      currentVariant?.function,
      currentVariant?.logo?.path,
    ]
  );
  return (
    <picture
      tw="[svg]:(h-32 w-auto)"
      onClick={() => pickLegacyOverlay(overlay)}
      data-testid="overlay-preview"
    >
      <div
        dangerouslySetInnerHTML={{
          __html: SVGComponent(overlay.svg),
        }}
      />
    </picture>
  );
};

/**
 * A v2 template tile. The canvas cannot draw one of these -- they are rendered by the server -- so
 * the tile is a label rather than a miniature, and picking it asks the server for a preview of THIS
 * variant's own photo and text.
 */
/**
 * SYM-6060. Said once, under the tiles, not on each of them.
 *
 * ⚠️ Wording is Steven's and Mark's, not a developer's, and it is one string on purpose so it can
 * be changed without hunting. It names no tier deliberately: the small set is not one tier but
 * seven of them plus every SCALE plan under EUR 4 000 a year, so a customer already on a plan
 * called Silver can be in it.
 */
const LOCKED_MESSAGE = 'Upgrade for more designs';

/**
 * A v2 template tile. The canvas cannot draw one of these -- they are rendered by the server -- so
 * the tile is a label rather than a miniature, and picking it asks the server for a preview of THIS
 * variant's own photo and text.
 */
const TemplatePreviewTile = ({ overlay }: { overlay: AdOverlay }) => {
  const { currentVariant, selectTemplate, templatePreviews } =
    useAdEditorContext();

  const code = overlay.template_code ?? '';
  const isSelected = currentVariant?.template === code;
  const preview = templatePreviews[code];

  /**
   * SYM-6060. A design this customer's plan does not include. Shown rather than left out, so the
   * cheapest plans can see what the rest of the set looks like -- but not choosable, and never
   * asked for as a preview, because the server refuses to draw it.
   *
   * Absent means available: an older server does not send the field, and a customer must never be
   * shown a lock because a deploy is half done.
   */
  const isLocked = overlay.available === false;

  /**
   * A locked tile still shows the design, faded. Steven and Mark settled that on 26 Aug 2026: the
   * point of showing these at all is that the customer can see what they would be buying, and a
   * grey box sells nothing. The message is said once under the grid rather than four times over
   * the designs it is selling.
   *
   * Not clickable, and not merely undecorated: no onClick at all, so there is no path from here to
   * selectTemplate. The server refuses the design on the write path regardless, but a control that
   * looks live and does nothing is its own bug -- this picker has shipped one before (SYM-5917).
   *
   * It still shows as SELECTED when the ad is on it. That only happens to an ad built before its
   * owner's plan stopped including the design, and those keep working on purpose -- so the picker
   * has to be able to say which design the customer is looking at. Marking it unselectable and
   * unselected at once left them unable to tell.
   */
  if (isLocked) {
    return (
      <TemplateTile
        data-testid="template-preview"
        data-template={code || undefined}
        data-locked="true"
        data-selected={isSelected ? 'true' : undefined}
        aria-disabled="true"
        $selected={isSelected}
        tw="relative cursor-default"
      >
        {preview?.url ? (
          <TemplateTileImage
            src={preview.url}
            alt={`${code} template`}
            draggable={false}
            tw="opacity-40 select-none"
          />
        ) : preview?.error ? (
          <span tw="text-xs text-red-600 text-center px-2">
            {preview.error}
          </span>
        ) : preview?.waiting ? (
          /*
           * SYM-6247. Seen on QA, 18 Sep 2026: during a per-user wait the four locked designs read
           * "Rendering…" next to four available ones saying "Busy right now". Same wait, same notice.
           */
          <span tw="text-xs text-gray-500 text-center px-2">
            Busy right now. Trying again shortly.
          </span>
        ) : (
          <span tw="text-xs text-gray-500">Rendering…</span>
        )}

        {/*
          ⚠️ THIS LAYER IS NOT DECORATION. It covers the picture so the browser hit-tests it
          instead, which is what stops "save image as" and dragging the file out.

          A preview is a finished ad in a design the customer has not bought. When a locked tile
          was a grey box, the grey itself covered the image and did that job; showing the real
          design removed the cover, and on QA on 26 Aug both right-click-save and drag-to-desktop
          handed over the full-size ad. Removing this puts that back.
        */}
        <span tw="absolute inset-0" aria-hidden="true" />
      </TemplateTile>
    );
  }

  return (
    <TemplateTile
      onClick={() => selectTemplate(overlay)}
      data-testid="template-preview"
      data-template={code || undefined}
      $selected={isSelected}
    >
      {preview?.url ? (
        <TemplateTileImage src={preview.url} alt={`${code} template`} />
      ) : preview?.error ? (
        <span tw="text-xs text-red-600 text-center px-2">{preview.error}</span>
      ) : preview?.waiting ? (
        // SYM-6247. The server asked for a wait of up to a minute and the retry is arranged: a calm
        // notice, not a spinner that reads as a hang and not a red message nobody can act on.
        <span tw="text-xs text-gray-500 text-center px-2">
          Busy right now. Trying again shortly.
        </span>
      ) : (
        /*
         * SYM-6214. Two different waits, and they are not the same promise to the customer.
         *
         * "Preparing" means the SERVER is drawing this design on the queue after a save, and the
         * editor is re-asking the ad list for it -- nobody has to click anything. "Rendering" is
         * the older wait: this editor asked just now, for an ad the server has not prepared.
         *
         * The map has to EXIST before its absence means anything: a server from before this ticket
         * sends no `template_previews` at all, and calling that "preparing" would promise a picture
         * that is never coming.
         */
        <span tw="text-xs text-gray-500">
          {currentVariant?.template_previews &&
          missingTileCodes(currentVariant, [code]).length
            ? 'Preparing…'
            : 'Rendering…'}
        </span>
      )}
    </TemplateTile>
  );
};

export const PlacementMenu = () => {
  const {
    currentVariant,
    defaultAdOverlays,
    customAdOverlays,
    canvasWidth,
    canvasHeight,
    ensureTemplatePreviews,
    templatePreviews,
  } = useAdEditorContext();

  // A tile is only usable where its template can actually render. The six templates are 1080x1920,
  // so they are story-only until 4:5 geometry exists (SYM-5817) -- offering one on a feed variant
  // would let a customer pick a template and get their old ad back.
  const forThisPlacement = (overlays: AdOverlay[]) =>
    overlays.filter(
      (overlay) => currentVariant?.placement === overlay.placement
    );

  const isV2 = (overlay: AdOverlay) => Boolean(overlay.template_code);

  const defOverlays = useMemo(
    () => forThisPlacement(defaultAdOverlays).filter((o) => !isV2(o)),
    [defaultAdOverlays, currentVariant?.placement]
  );

  /**
   * SYM-5860. Also gated on the creative being an IMAGE, not only on the placement.
   *
   * A story VIDEO has `placement === 'story'`, so it passed the placement filter and the six tiles
   * stayed on screen: open the Overlays tab on an image variant, click a video, and the templates
   * were still offered. They compose a still image, so there is nothing correct for them to do on
   * a video. The server refuses it now too, which is the half that matters -- hiding a control
   * never stops a request.
   */
  const v2Overlays = useMemo(
    () =>
      variantCanUseTemplates(
        currentVariant?.placement,
        currentVariant?.creative_type
      )
        ? forThisPlacement(defaultAdOverlays).filter(isV2)
        : [],
    [
      defaultAdOverlays,
      currentVariant?.placement,
      currentVariant?.creative_type,
    ]
  );

  // Custom and catalogue overlays come from different endpoints today, so no custom row carries a
  // template_code -- but if one ever did, rendering it through OverlayPreview would try to composite
  // a server-rendered template onto the canvas. Filter by the same rule rather than relying on that
  // staying true.
  const cusOverlaysForPlacement = useMemo(
    () => forThisPlacement(customAdOverlays).filter((o) => !isV2(o)),
    [customAdOverlays, currentVariant?.placement]
  );

  /**
   * SYM-5794. The curation ends in ONE set, not "New Templates" stacked on top of the old list.
   *
   * SYM-6051: read from the subscription rather than from the feature-flag tool. It is the same
   * answer -- the server resolves that flag -- but now there is only one reader of it, so the
   * section the customer sees and the catalogue the server sends cannot describe different
   * customers. One value here also keeps this section and the tiles below from disagreeing and
   * leaving the panel empty, which is why it was read directly in the first place.
   */
  const { hasAdTemplates: hasTemplatesV2 } = useNavigationContext();
  // SYM-5917: also gated on the creative being able to CARRY an overlay. A video has no fabric
  // canvas, so picking one of these tiles has never done anything for anyone, flag or no flag.
  // SYM-6252. A customer with templates v2 reads "template" everywhere in this panel; the others
  // keep "overlay", which is what their panel holds (decision Steven, 18 Sep 2026).
  const noun = hasTemplatesV2 ? 'template' : 'overlay';
  const Noun = hasTemplatesV2 ? 'Template' : 'Overlay';

  const showLegacyDefaults = showsLegacyDefaults(
    hasTemplatesV2,
    v2Overlays.length,
    variantCanTakeOverlay(currentVariant?.creative_type)
  );

  /**
   * SYM-6010. Custom artwork is only offered where it actually fits the frame.
   *
   * This section is the one the v2 switch never touched. The standard overlays stop being rendered
   * for a v2 customer entirely (showLegacyDefaults, below), but custom artwork sits behind its own
   * flag and stayed on offer -- drawn for a square, in a frame that is now portrait. Nothing in the
   * compositing path scales an overlay, so picking one puts the artwork in the top-left and leaves
   * the bottom quarter of the ad bare.
   *
   * ⚠️ The hiding is gated on hasTemplatesV2 and the sizing is NOT, and that asymmetry is the point.
   * A customer who never opted in sees exactly what they saw yesterday -- the SYM-5792 guarantee.
   * Measured on production 2026-08-24: all five pieces of custom feed artwork are 1000x1000 and all
   * five story pieces are 1080x1920, so this hides nothing that anyone is using today and only
   * bites once a customer is switched on.
   */
  const cusOverlays = useMemo(
    () =>
      cusOverlaysForPlacement.filter((o) =>
        overlayIsOfferable(
          getOverlayFit(o.svg, canvasWidth, canvasHeight),
          Boolean(hasTemplatesV2)
        )
      ),
    [cusOverlaysForPlacement, canvasWidth, canvasHeight, hasTemplatesV2]
  );

  const hiddenCusOverlayCount =
    cusOverlaysForPlacement.length - cusOverlays.length;

  /**
   * SYM-6213. The other designs are asked for HERE, when the panel is on screen.
   *
   * The provider still requests the selected design as soon as the editor has an ad, because the
   * canvas needs it whatever tab is open -- that is why fetching moved out of this component once.
   * But it used to ask for every design with it, on every ad clicked, and the panel was the only
   * place anyone looked at them. Eight or nine renders per click is part of what took production
   * down on 2026-09-15.
   *
   * Re-run when the slots change, not only on mount: a typed value drops the unselected slots so
   * they redraw, and while the panel is open this is what redraws them.
   */
  const v2Codes = v2Overlays
    .map((overlay) => overlay.template_code)
    .filter(Boolean) as string[];
  const v2CodesKey = v2Codes.join(',');

  useEffect(() => {
    if (!hasTemplatesV2 || !currentVariant?.path || !v2Codes.length) return;
    ensureTemplatePreviews?.(v2Codes);
  }, [
    hasTemplatesV2,
    v2CodesKey,
    currentVariant?.uuid,
    currentVariant?.path,
    templatePreviews,
  ]);

  return (
    <div tw="flex flex-col gap-12" id="placement-menu">
      {/* CUSTOM OVERLAYS */}
      {currentVariant?.path ? (
        <>
          {/*
            NEW TEMPLATES. Gated on the answer the SERVER resolved and sent down with the
            subscription, not on a second reading of the feature flag from the browser -- so the
            client and the server cannot describe different customers.

            Which designs appear here needs no rule of its own: the catalogue the server returns
            already contains only the ones this customer's plan may use.
          */}
          {hasTemplatesV2 ? (
            v2Overlays.length ? (
              <div>
                <Title3 tw="text-2xl font-bold">New Templates</Title3>
                <PlacementMenuOptionContainer>
                  {v2Overlays.map((overlay) => (
                    <TemplatePreviewTile key={overlay.id} overlay={overlay} />
                  ))}
                </PlacementMenuOptionContainer>

                {/*
                  SYM-6060. One nudge for the whole set, and only when something is actually
                  locked. A customer on the full set has nothing withheld, so they must never see
                  it -- and neither must anyone whose catalogue came back without the field at all,
                  which is what `=== false` guarantees.
                */}
                {v2Overlays.some((overlay) => overlay.available === false) && (
                  <UpgradeNudge data-testid="upgrade-nudge">
                    {LOCKED_MESSAGE}
                  </UpgradeNudge>
                )}

                {/*
                  SYM-5852. Directly under the tiles rather than in its own tab: the values belong
                  to the template that was just chosen, and a customer who has picked B2 has no
                  reason to hunt for where to type a salary.
                */}
                {currentVariant?.template && (
                  <div tw="mt-6">
                    <TemplateValueControls />
                  </div>
                )}
              </div>
            ) : null
          ) : null}

          <div>
            <Title3 tw="text-2xl font-bold">Custom {Noun}s</Title3>
            <PostHogFeature
              flag="custom-overlays"
              match={true}
              fallback={
                <span tw="text-gray-700">
                  Sounds interesting? <br />
                  Contact us via the chat button.
                </span>
              }
            >
              {cusOverlays.length ? (
                <PlacementMenuOptionContainer>
                  {cusOverlays &&
                    cusOverlays.map((overlay) => (
                      <OverlayPreview key={overlay.id} overlay={overlay} />
                    ))}
                </PlacementMenuOptionContainer>
              ) : (
                !hiddenCusOverlayCount && (
                  <span tw="text-gray-700">
                    You have no custom {noun}s yet. <br />
                    Get started below.
                  </span>
                )
              )}

              {/*
                SYM-6010. Say why artwork is missing rather than letting it vanish.
                "You have none" would be a lie to a customer who has three and cannot see them, and
                the only way back is a conversation with us -- these are drawn by sympl, not
                uploaded, so there is no self-serve fix to point at.
              */}
              {hiddenCusOverlayCount > 0 && (
                <span tw="text-gray-700" data-testid="custom-overlays-hidden">
                  {hiddenCusOverlayCount === 1
                    ? `1 of your custom ${noun}s was drawn for a differently shaped ad and is not shown here.`
                    : `${hiddenCusOverlayCount} of your custom ${noun}s were drawn for a differently shaped ad and are not shown here.`}
                  <br />
                  Contact us via the chat button to have them redrawn.
                </span>
              )}
            </PostHogFeature>
          </div>

          {showLegacyDefaults && (
            <div>
              <Title3 tw="text-2xl font-bold">Default Overlays</Title3>

              <PlacementMenuOptionContainer>
                {defOverlays.map((overlay) => (
                  <OverlayPreview key={overlay.id} overlay={overlay} />
                ))}
              </PlacementMenuOptionContainer>
            </div>
          )}
        </>
      ) : (
        <div>
          <span>Select an image before picking a {noun}.</span>
        </div>
      )}
      <div tw="flex flex-col items-center mt-8">
        <Button
          onClick={() => {
            fireEvent('custom_overlay_interested');
            showIntercom(
              `Hi, I want to use my own custom ${noun}s. Help me out.`
            );
          }}
        >
          Add your own {noun}
        </Button>
      </div>
    </div>
  );
};

const PlacementMenuOptionContainer = styled.div(
  tw`grid grid-cols-1 xl:grid-cols-2 gap-4 overflow-y-auto p-4 place-items-center cursor-pointer`
);

/**
 * SYM-6060. The one nudge under the tiles. A statement, not a button: nothing happens on click,
 * and it does not pretend otherwise.
 */
const UpgradeNudge = styled.div(
  tw`mx-4 mb-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-center text-sm font-semibold text-gray-700`
);

const TemplateTile = styled.div<{ $selected: boolean }>(({ $selected }) => [
  tw`flex flex-col items-center justify-center gap-1 h-32 w-full overflow-hidden rounded-lg border-2 cursor-pointer`,
  $selected ? tw`border-blue-600 bg-blue-50` : tw`border-gray-200 bg-white`,
]);

// The templates are 9:16; `contain` keeps the whole ad visible in the tile rather than cropping it
// to a square, which is the point of showing a picture at all.
const TemplateTileImage = styled.img(tw`h-full w-auto object-contain`);
